import React, { useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { isEmpty } from 'lodash';

import { PopperComponent } from '../UI';
import { SearchAssetsInput } from './HistoricalIndex/SearchAssetsInput';
import { SEARCH_ASSETS } from '../../http/apollo-queries';
import { useDidMountEffect } from '../../hooks/useDidMountEffect';
import { IAsset } from '../../types/model';

type SetValueType = React.Dispatch<React.SetStateAction<string | undefined>>;

interface SearchSuggestionsProps {
  onSuggestionClick: (suggestion: IAsset) => void;
  className?: string;
  filter?: (args: IAsset[]) => IAsset[];
}

export const SearchAssetSuggestions = ({
  onSuggestionClick,
  className = '',
  filter,
}: SearchSuggestionsProps) => {
  const searchInputRef = useRef<{ setValue: SetValueType } | null>(null);

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);

  // Queries
  const [
    searchAssets,
    {
      data: { searchAssets: { items: suggestions = [] } = {} } = {},
      loading: loadingSearch,
    },
  ] = useLazyQuery<{
    searchAssets: { items: IAsset[] };
  }>(SEARCH_ASSETS, {
    fetchPolicy: 'network-only',
  });

  // Effects
  useDidMountEffect(() => {
    if (!searchTerm) {
      setIsSuggestionsVisible(false);
      return;
    }
    searchAssets({
      variables: {
        term: searchTerm,
      },
    })
      .then(({ data }) => {
        if (isEmpty(data?.searchAssets.items || [])) return;

        setIsSuggestionsVisible(true);
      })
      .catch((e) => console.error('SearchAssetSuggestions::', e.toString()));
  }, [searchAssets, searchTerm]);

  return (
    <PopperComponent
      reference={
        <div
          className={`${className} flex h-14 border border-transparent`}
          onClick={() => {
            if (!isEmpty(suggestions)) setIsSuggestionsVisible(true);
          }}
        >
          <SearchAssetsInput
            onFinishTyping={(v) => {
              !v && setIsSuggestionsVisible(false);
              setSearchTerm(v);
            }}
            loading={loadingSearch}
            ref={searchInputRef}
            size={'full'}
          />
        </div>
      }
      visible={isSuggestionsVisible}
    >
      <div
        className={`
          border
          border-grey-30
          rounded-xl
          px-4
          pb-4
          bg-white
          dark:bg-grey-90
          w-96
          max-h-72
          overflow-y-auto
          scrollbar-hide
          overflow-x-hidden
          shadow-light
        `}
        onMouseLeave={() => {
          setIsSuggestionsVisible(false);
        }}
      >
        {suggestions.map((suggestion) => {
          const { id, exchange, name, type } = suggestion;

          return (
            <div
              className={'flex text-sm pt-4'}
              key={id}
              onMouseLeave={(e) => e.stopPropagation()}
              onClick={() => {
                onSuggestionClick(suggestion);
                setIsSuggestionsVisible(false);
                searchInputRef.current?.setValue('');
              }}
            >
              <p>{name.slice(0, 15)}</p>
              <p className="ml-2 text-grey-50">
                {`${exchange ? ` - ${exchange}` : ''} - ${type}`}
              </p>
            </div>
          );
        })}
      </div>
    </PopperComponent>
  );
};
